import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Card,
  CardBody,
  CardHeader,
  Divider,
  CircularProgress,
  CircularProgressLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue
} from '@chakra-ui/react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import {
  isReturnContext,
  ReturnContext,
  readSessionStorageRecord,
  validateReturnUrl,
} from '../utils/testFlowSecurity';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface PredictionResponse {
  enneagram_type: number;
  wing: number;
  type_probabilities: number[];
  wing_probabilities: number[];
  session_id?: string;
}

interface LearningProfile {
  number: number;
  name: string;
  description: string;
  classroom: string;
  teamwork: string;
  support: string;
}

// Perfiles orientados al ámbito educativo: rasgos de aprendizaje y pautas de acompañamiento.
const enneagramTypes: LearningProfile[] = [
  {
    number: 1, name: "Perfeccionista",
    description: "Perfil metódico y responsable, con un alto estándar de calidad. Valora hacer las cosas bien y se guía por criterios claros de lo correcto.",
    classroom: "Trabaja con orden y constancia, cuida los detalles y cumple los plazos. Puede ser muy autoexigente ante el error.",
    teamwork: "Aporta rigor y organización al grupo. Conviene practicar la flexibilidad cuando otros abordan la tarea de otra manera.",
    support: "Reforzar el progreso y no solo el resultado, normalizar el error como parte del aprendizaje y ofrecer criterios de evaluación explícitos."
  },
  {
    number: 2, name: "Colaborador",
    description: "Perfil empático y orientado a las personas. Se implica en el bienestar del grupo y se motiva con el vínculo y la cooperación.",
    classroom: "Aprende bien en entornos cooperativos y con retroalimentación cercana. Puede dejar sus propias tareas por atender a los demás.",
    teamwork: "Facilita la cohesión y el apoyo entre compañeros. Conviene trabajar la asertividad y saber pedir ayuda.",
    support: "Reconocer su esfuerzo individual, asignar roles de apoyo entre iguales y ayudarle a fijar sus propios objetivos."
  },
  {
    number: 3, name: "Orientado a objetivos",
    description: "Perfil eficaz y competitivo, motivado por metas claras y por ver resultados. Se adapta con rapidez y busca el logro.",
    classroom: "Rinde bien con objetivos concretos y seguimiento visible. Puede priorizar la nota sobre la comprensión profunda.",
    teamwork: "Impulsa al equipo y organiza tareas. Conviene equilibrar el resultado con el proceso y con el ritmo de los demás.",
    support: "Plantear retos graduales, valorar el aprendizaje más allá de la calificación y fomentar la autoevaluación."
  },
  {
    number: 4, name: "Creativo",
    description: "Perfil sensible y reflexivo, con una fuerte necesidad de autenticidad. Conecta con la expresión personal y la creatividad.",
    classroom: "Destaca en tareas abiertas, expresivas y con sentido personal. Puede desmotivarse ante actividades muy mecánicas.",
    teamwork: "Aporta ideas originales y una mirada distinta. Conviene concretar las propuestas y ajustarse a los plazos comunes.",
    support: "Ofrecer margen de personalización en los proyectos, escucha activa y ayuda para gestionar los momentos de bajada anímica."
  },
  {
    number: 5, name: "Analítico",
    description: "Perfil observador e independiente, con gran curiosidad intelectual. Necesita comprender en profundidad antes de participar.",
    classroom: "Aprende con autonomía, investigando y profundizando. Puede participar poco en clase si no se siente seguro con el tema.",
    teamwork: "Aporta análisis y conocimiento. Conviene animarle a compartir sus avances y a pedir opinión durante el proceso.",
    support: "Dar tiempo de preparación, proponer proyectos de indagación y crear espacios seguros para exponer sus ideas."
  },
  {
    number: 6, name: "Comprometido",
    description: "Perfil leal, prudente y responsable. Valora la seguridad, la coherencia y la confianza en el grupo y en las figuras de referencia.",
    classroom: "Rinde mejor con instrucciones claras y rutinas estables. Puede dudar de sus capacidades ante situaciones nuevas.",
    teamwork: "Es fiable y anticipa problemas. Conviene reforzar la confianza en sus propias decisiones.",
    support: "Comunicar expectativas con claridad, anticipar los cambios y dar retroalimentación frecuente y constructiva."
  },
  {
    number: 7, name: "Dinámico",
    description: "Perfil entusiasta y versátil, con muchos intereses. Se motiva con la novedad, la variedad y las posibilidades de aprender.",
    classroom: "Participa con energía y conecta ideas con facilidad. Puede dispersarse o dejar tareas sin terminar.",
    teamwork: "Aporta optimismo y propuestas. Conviene trabajar la constancia y el cierre de cada tarea.",
    support: "Variar las metodologías, dividir el trabajo en hitos cortos y acompañar la planificación hasta la entrega."
  },
  {
    number: 8, name: "Líder",
    description: "Perfil decidido y directo, con iniciativa y sentido de la justicia. Se implica cuando siente autonomía y responsabilidad.",
    classroom: "Toma la iniciativa y afronta retos con determinación. Puede chocar con normas que percibe como arbitrarias.",
    teamwork: "Asume responsabilidades y protege al grupo. Conviene cuidar la escucha y el tono al dirigir a otros.",
    support: "Explicar el sentido de las normas, asignar responsabilidades reales y canalizar su energía hacia proyectos de liderazgo."
  },
  {
    number: 9, name: "Mediador",
    description: "Perfil tranquilo y conciliador, que busca la armonía. Aporta estabilidad y sabe integrar puntos de vista distintos.",
    classroom: "Trabaja con calma y constancia en ambientes estables. Puede posponer decisiones o pasar desapercibido en el grupo.",
    teamwork: "Media entre posiciones y reduce tensiones. Conviene animarle a expresar su propia opinión.",
    support: "Fomentar su participación con preguntas directas, fijar prioridades concretas y reconocer sus aportaciones."
  }
];

const getEnneagramInfo = (type: number) => {
  return enneagramTypes.find(t => t.number === type) || enneagramTypes[0];
};


const Results: React.FC = () => {
  // Hooks deben ir siempre al inicio
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBgHighlight = useColorModeValue('blue.50', 'blue.900');
  const radarMainColor = useColorModeValue('rgba(4,90,105,1)', 'rgba(79,209,209,1)');
  const radarMainBg = useColorModeValue('rgba(4,90,105,0.2)', 'rgba(79,209,209,0.2)');
  const radarWingColor = useColorModeValue('rgba(56,178,172,1)', 'rgba(81,230,220,1)');
  const radarWingBg = useColorModeValue('rgba(56,178,172,0.15)', 'rgba(81,230,220,0.15)');
  const radarGridColor = useColorModeValue('#CBD5E0', '#4A5568');
  const radarLabelColor = useColorModeValue('#2D3748', '#E2E8F0');

  const [results, setResults] = useState<PredictionResponse | null>(null);
  const [returnContext, setReturnContext] = useState<ReturnContext>({});
  const navigate = useNavigate();
  const currentOrigin = window.location.origin;
  const hasValidReturnTarget = Boolean(validateReturnUrl(returnContext.returnUrl, currentOrigin));
  
  useEffect(() => {
    // Get results from sessionStorage
    const storedResults = sessionStorage.getItem('enneagramResults');
    
    if (storedResults) {
      try {
        const parsedResults = JSON.parse(storedResults);
        setResults(parsedResults);
      } catch (err) {
        console.error('Error parsing results:', err);
      }
    }

    const storedReturnContext = readSessionStorageRecord(
      'testReturnContext',
      currentOrigin,
      isReturnContext
    );

    if (storedReturnContext) {
      setReturnContext(storedReturnContext);
    }
  }, [currentOrigin]);
  
  const handleRetakeTest = () => {
    // Clear stored results
    sessionStorage.removeItem('enneagramResults');
    sessionStorage.removeItem('testReturnContext');
    navigate('/test');
  };

  const handleReturnToStudentPanel = () => {
    const targetReturnUrl = validateReturnUrl(returnContext.returnUrl, currentOrigin);

    if (!targetReturnUrl) {
      navigate('/');
      return;
    }

    const redirectUrl = new URL(targetReturnUrl, window.location.origin);

    if (returnContext.studentId) {
      redirectUrl.searchParams.set('studentId', returnContext.studentId);
    }

    redirectUrl.searchParams.set('mainType', returnContext.mainType || String(results?.enneagram_type || ''));
    redirectUrl.searchParams.set('wingType', returnContext.wingType || String(results?.wing || ''));

    if (returnContext.sessionId) {
      redirectUrl.searchParams.set('sessionId', returnContext.sessionId);
    }

    sessionStorage.removeItem('enneagramResults');
    sessionStorage.removeItem('testReturnContext');
    sessionStorage.removeItem('testStudentContext');
    window.location.assign(redirectUrl.toString());
  };
  
  if (!results) {
    return (
      <Alert
        status="info"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        height="200px"
        borderRadius="md"
      >
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          No hay resultados disponibles
        </AlertTitle>
        <AlertDescription maxWidth="sm">
          No se han encontrado resultados del test. Por favor, realiza el test para ver tus resultados.
          <Button 
            mt={4}
            colorScheme="blue"
            onClick={() => navigate('/test')}
          >
            Ir al Test
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  
  const mainType = getEnneagramInfo(results.enneagram_type);
  const wingType = getEnneagramInfo(results.wing);
  
  // Calculate percentage for main type and wing
  const mainTypePercentage = Math.round(results.type_probabilities[results.enneagram_type - 1] * 100);
  const wingTypePercentage = Math.round(results.wing_probabilities[results.wing - 1] * 100);
  
  // Radar chart data for types
  const radarData = {
    labels: enneagramTypes.map(t => `Perfil ${t.number}\n${t.name}`),
    datasets: [
      {
        label: 'Probabilidad Tipo',
        data: results.type_probabilities.map(p => Math.round(p * 100)),
        backgroundColor: radarMainBg,
        borderColor: radarMainColor,
        pointBackgroundColor: radarMainColor,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: radarMainColor,
      },
      {
        label: 'Probabilidad Ala',
        data: results.wing_probabilities.map(p => Math.round(p * 100)),
        backgroundColor: radarWingBg,
        borderColor: radarWingColor,
        pointBackgroundColor: radarWingColor,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: radarWingColor,
      }
    ]
  };
  const radarOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { color: radarLabelColor }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.formattedValue}%`
        }
      }
    },
    scales: {
      r: {
        angleLines: { color: radarGridColor },
        grid: { color: radarGridColor },
        pointLabels: { color: radarLabelColor, font: { size: 14 } },
        ticks: {
          color: radarLabelColor,
          stepSize: 20,
          callback: (v: any) => `${v}%`
        },
        min: 0,
        max: 100
      }
    }
  };
  
  return (
    <Box>
      <Heading mb={2} color="primary.600">Resultados del cuestionario</Heading>
      <Text mb={8} fontSize="lg">
        Este es tu perfil de aprendizaje según tus respuestas. Es una orientación para el acompañamiento educativo, no una calificación ni un diagnóstico.
      </Text>
      
      {/* Main Results */}
      <Card bg={cardBgHighlight} mb={8}>
        <CardBody>
          <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="space-between">
            <VStack align={{ base: 'center', md: 'start' }} spacing={3} flex={1}>
              <Heading size="lg">
                Perfil {mainType.number}: {mainType.name}
              </Heading>
              <Heading size="md" color="gray.600">
                con influencia del perfil {wingType.number} ({wingType.name})
              </Heading>
              <Text fontSize="lg">{mainType.description}</Text>
            </VStack>
            
            <HStack spacing={6} mt={{ base: 6, md: 0 }}>
              <CircularProgress 
                value={mainTypePercentage} 
                size="120px" 
                color="primary.500" 
                thickness="8px"
              >
                <CircularProgressLabel>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" fontSize="xl">{mainTypePercentage}%</Text>
                    <Text fontSize="sm">Perfil {mainType.number}</Text>
                  </VStack>
                </CircularProgressLabel>
              </CircularProgress>
              
              <CircularProgress 
                value={wingTypePercentage} 
                size="120px" 
                color="teal.400" 
                thickness="8px"
              >
                <CircularProgressLabel>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" fontSize="xl">{wingTypePercentage}%</Text>
                    <Text fontSize="sm">Influencia {wingType.number}</Text>
                  </VStack>
                </CircularProgressLabel>
              </CircularProgress>
            </HStack>
          </Flex>
        </CardBody>
      </Card>
      
      {/* Detailed Results - Radar Chart */}
      <Heading size="md" mb={4}>Distribución del perfil</Heading>
      <Card bg={cardBg} mb={8}>
        <CardBody>
          <Box w={{ base: '100%', md: '70%' }} mx="auto">
            <Radar data={radarData} options={radarOptions} style={{ maxHeight: 400 }} />
          </Box>
        </CardBody>
      </Card>
      
      {/* Understanding Your Type */}
      <Card bg={cardBg} mb={8}>
        <CardHeader>
          <Heading size="md">Cómo interpretar tu perfil ({mainType.number}w{wingType.number})</Heading>
        </CardHeader>
        <CardBody>
          <Text mb={4}>
            Tu perfil principal es el {mainType.number}, con influencia del {wingType.number}. Estos son los rasgos de cada uno y cómo se traducen en el aprendizaje:
          </Text>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Heading size="sm" mb={2} color="primary.600">Perfil principal ({mainType.number} · {mainType.name})</Heading>
              <Text mb={2}>{mainType.description}</Text>
              <Text><b>En el aula:</b> {mainType.classroom}</Text>
              <Text><b>En el trabajo en equipo:</b> {mainType.teamwork}</Text>
              <Text><b>Cómo acompañar el aprendizaje:</b> {mainType.support}</Text>
            </Box>
            <Divider />
            <Box>
              <Heading size="sm" mb={2} color="teal.600">Perfil de influencia ({wingType.number} · {wingType.name})</Heading>
              <Text mb={2}>{wingType.description}</Text>
              <Text><b>En el aula:</b> {wingType.classroom}</Text>
              <Text><b>En el trabajo en equipo:</b> {wingType.teamwork}</Text>
              <Text><b>Cómo acompañar el aprendizaje:</b> {wingType.support}</Text>
            </Box>
          </VStack>
        </CardBody>
      </Card>
      
      {/* Action Buttons */}
      <Flex justify="center" gap={4}>
        <Button 
          colorScheme="blue" 
          onClick={handleRetakeTest}
        >
          Repetir el cuestionario
        </Button>
        <Button 
          onClick={handleReturnToStudentPanel}
          colorScheme="gray"
        >
          {hasValidReturnTarget ? 'Volver al tablero del alumno' : 'Volver al inicio'}
        </Button>
      </Flex>
    </Box>
  );
};

export default Results;
